export type DirectionBlock =
  | { kind: 'cycleHeading'; groupIndex: number }
  | { kind: 'placeRow'; groupIndex: number; placeIndex: number; rowIndex: number }
  | { kind: 'placeSubtotal'; groupIndex: number; placeIndex: number }
  | { kind: 'groupTotal'; groupIndex: number }
  | { kind: 'deleted'; deletedIndex: number }
  | { kind: 'memo' };

// コース（1 shift）の描画対象を「行レベル」の順序付きブロック列に変換する。
// 地点(place)の明細は 1 行ずつ独立ブロック（placeRow）にし、行単位で改ページ可能にする。
// 並び順: 各 group で showCycleHeading?→cycleHeading、各 place で placeRow×行数 ＋ hasSubtotal?→placeSubtotal、
//         group 末に showGroupTotal?→groupTotal。全 group 後に deleted×deletedCount、最後に hasMemo?→memo。
export function buildDirectionBlocks(params: {
  // [groupIndex][placeIndex] = mergeBySameRow 後の表示行数。
  placeRowCounts: number[][];
  // [groupIndex][placeIndex] = その地点に小計を出すか（renderPlaceBlock の小計表示条件）。
  hasSubtotal: boolean[][];
  showCycleHeading: boolean;
  showGroupTotal: boolean;
  deletedCount: number;
  // 備考欄を末尾ブロックとして 1 つ加えるか。
  hasMemo: boolean;
}): DirectionBlock[] {
  const {
    placeRowCounts, hasSubtotal, showCycleHeading, showGroupTotal, deletedCount, hasMemo,
  } = params;
  const blocks: DirectionBlock[] = [];
  placeRowCounts.forEach((placesInGroup, groupIndex) => {
    if (showCycleHeading) blocks.push({ kind: 'cycleHeading', groupIndex });
    placesInGroup.forEach((rowCount, placeIndex) => {
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        blocks.push({
          kind: 'placeRow', groupIndex, placeIndex, rowIndex,
        });
      }
      if (hasSubtotal[groupIndex]?.[placeIndex]) {
        blocks.push({ kind: 'placeSubtotal', groupIndex, placeIndex });
      }
    });
    if (showGroupTotal) blocks.push({ kind: 'groupTotal', groupIndex });
  });
  for (let deletedIndex = 0; deletedIndex < deletedCount; deletedIndex += 1) {
    blocks.push({ kind: 'deleted', deletedIndex });
  }
  if (hasMemo) blocks.push({ kind: 'memo' });
  return blocks;
}

// useMeasuredPages の再計測ガード（measuredFor）に渡す depsKey を組み立てる。
// 同一 shift でも、印刷レイアウト（ページ分割・ページ番号・余白）に効く表示構成
// （積/降の action、表示列、備考メモ、ブロック構成・keepWithNext）が変われば
// 必ず別キーになり、再計測がスキップされず確定描画が追従する。
export function buildMeasureDepsKey(params: {
  // 対象 shift の ID。shift 切替時の再計測キー。
  shiftId: number | string;
  // 積/降（行の値が変わり、行高に影響しうる）。
  action: string | null;
  // 表示列の見出し（列構成が変わると列幅・行高が変わる）。
  headers: string[];
  // 備考メモ本文（メモブロックの高さが本文長で変わる。空なら null）。
  memo: string | null;
  // 行レベルのブロック列（構成が変わると計測対象・分割が変わる）。
  blocks: DirectionBlock[];
  // 見出しを次ブロックと同ページに留める指定（分割結果に効く）。
  keepWithNext: boolean[];
}): string {
  return JSON.stringify(params);
}

// 描画用セグメント。連続する同一地点の placeRow をまとめた run と、それ以外の単独ブロック。
export type BlockSegment =
  | {
    kind: 'placeRun';
    groupIndex: number;
    placeIndex: number;
    // run を構成する各行（元 blocks の index と地点内 rowIndex）。
    rows: { blockIndex: number; rowIndex: number }[];
  }
  | { kind: 'single'; blockIndex: number };

// blocks のうち indices で指定された並びを、描画セグメント列に変換する。
// 連続する同一 (groupIndex, placeIndex) の placeRow を 1 つの placeRun にまとめ、
// グループ列を run 先頭で rowSpan 表示する確定描画と、同じ run 文脈で測る計測描画で共有する。
// 間に非 placeRow が挟まれば run は分断される（= 確定描画と同じまとめ方）。
export function groupBlockRuns(blocks: DirectionBlock[], indices: number[]): BlockSegment[] {
  const segments: BlockSegment[] = [];
  let i = 0;
  while (i < indices.length) {
    const blockIndex = indices[i];
    const b = blocks[blockIndex];
    if (b.kind === 'placeRow') {
      const { groupIndex, placeIndex } = b;
      const rows: { blockIndex: number; rowIndex: number }[] = [];
      while (i < indices.length) {
        const curIndex = indices[i];
        const cur = blocks[curIndex];
        if (cur.kind !== 'placeRow' || cur.groupIndex !== groupIndex || cur.placeIndex !== placeIndex) break;
        rows.push({ blockIndex: curIndex, rowIndex: cur.rowIndex });
        i += 1;
      }
      segments.push({
        kind: 'placeRun', groupIndex, placeIndex, rows,
      });
      continue;
    }
    segments.push({ kind: 'single', blockIndex });
    i += 1;
  }
  return segments;
}

// paginateByHeights に渡す keepWithNext を組み立てる。
// - 回転見出し(cycleHeading): 次ブロックと同ページに留める（見出しがページ末尾に孤立しない）。
// - 小計(placeSubtotal)・合計(groupTotal)行の直前ブロック: それらが単独ページに孤立しないよう、
//   直前ブロックを同ページに留める（= 小計/合計を直前の行と必ず一緒に置く）。
export function buildKeepWithNext(blocks: DirectionBlock[]): boolean[] {
  return blocks.map((b, i) => {
    if (b.kind === 'cycleHeading') return true;
    const next = blocks[i + 1];
    return next !== undefined && (next.kind === 'groupTotal' || next.kind === 'placeSubtotal');
  });
}

export type PageMeta = { pageNumber: number; totalPages: number; blockIndices: number[] };

export function withPageNumbers(pages: number[][]): PageMeta[] {
  return pages.map((blockIndices, i) => ({ pageNumber: i + 1, totalPages: pages.length, blockIndices }));
}

// heights[i] = i番目ブロックの実測高さ。ブロックは不可分。available超えの単独ブロックは単独ページ。
// keepWithNext[i]===true のブロックは次ブロック i+1 と必ず同じページに置く（見出しがページ末尾に
// 孤立して次ページが見出し無しで始まるのを防ぐ）。i を置くと i がページ末尾になり、かつ i+1 が
// 同ページに入らない場合は、i の前で改ページして i から新ページに置く。i が最後の要素なら通常どおり。
export function paginateByHeights(
  heights: number[],
  available: number,
  keepWithNext?: boolean[],
): number[][] {
  const pages: number[][] = [];
  let current: number[] = [];
  let used = 0;
  heights.forEach((h, i) => {
    // 通常の改ページ判定: current に何か載っていて i を載せると溢れるなら改ページ。
    if (current.length > 0 && used + h > available) {
      pages.push(current);
      current = [];
      used = 0;
    }
    // 見出し系: i を載せると i がページ末尾になり次ブロックが同ページに入らないなら、
    // 先に改ページして i を新ページ先頭へ送る（i 単独で改ページしないよう current が空でない時のみ）。
    if (
      keepWithNext?.[i]
      && i + 1 < heights.length
      && current.length > 0
      && used + h + heights[i + 1] > available
    ) {
      // cycleHeading→明細→小計 のように keepWithNext が連鎖している場合、i だけを
      // 新ページへ送ると current 末尾の連鎖ブロック（見出し等）が前ページに孤立する。
      // current 末尾から keepWithNext=true が連続する範囲を i と一緒に新ページへ運ぶ。
      const carried: number[] = [];
      while (current.length > 0 && keepWithNext[current[current.length - 1]]) {
        carried.unshift(current.pop());
      }
      if (current.length > 0) pages.push(current);
      current = carried;
      used = carried.reduce((sum, idx) => sum + heights[idx], 0);
    }
    current.push(i);
    used += h;
  });
  if (current.length > 0) pages.push(current);
  return pages;
}
